import { useState, useEffect, useRef } from "react";
import OtpInput from "react-otp-input";

import { t } from "@/i18n-js/instance";

import { useOtpStyles } from "./useOtpStyles";

interface OTPInputProps {
  autoFocus?: boolean;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
  error?: boolean;
  inputClassName?: string;
  numInputs?: number;
  onChange?: (otp: string) => void;
  onComplete?: (otp: string) => void;
  onValueChange?: (otp: string) => void;
  value?: string;
}

export const OTPInput = ({
  autoFocus = true,
  className = "",
  containerClassName,
  disabled = false,
  error = false,
  inputClassName: customInputClassName,
  numInputs = 6,
  onChange,
  onComplete,
  onValueChange,
  value: controlledValue,
}: OTPInputProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  // Support both controlled and uncontrolled patterns
  const [internalValue, setInternalValue] = useState("");
  const isControlled = controlledValue !== undefined;
  const otp = isControlled ? controlledValue : internalValue;

  const { inputClassName: defaultInputClassName } = useOtpStyles({
    disabled,
    error,
  });

  const handleChange = (value: string) => {
    // Update internal state if uncontrolled
    if (!isControlled) {
      setInternalValue(value);
    }

    // Call all relevant callbacks
    onChange?.(value);
    onValueChange?.(value);

    if (value.length === numInputs) {
      onComplete?.(value);
    }
  };

  useEffect(() => {
    if (autoFocus && otp.length === 0 && !disabled && containerRef.current) {
      const firstInput =
        containerRef.current.querySelector<HTMLInputElement>("input");
      firstInput?.focus();
    }
  }, [autoFocus, otp, disabled]);

  useEffect(() => {
    if (error && !isControlled) {
      // Only auto-clear if uncontrolled (parent controls clearing in controlled mode)
      setInternalValue("");
    }
  }, [error, isControlled]);

  const finalInputClassName = customInputClassName || defaultInputClassName;
  const finalContainerClassName =
    containerClassName || "flex justify-between gap-2 sm:gap-4 sm:text-lg";

  return (
    <div className={className} ref={containerRef}>
      <fieldset className="contents">
        <legend className="sr-only">{t("otp.input.legend")}</legend>
        <OtpInput
          value={otp}
          onChange={handleChange}
          numInputs={numInputs}
          inputType="tel"
          // containerStyle accepts both CSSProperties object or className string
          containerStyle={finalContainerClassName}
          skipDefaultStyles
          renderInput={inputProps => (
            <input
              {...inputProps}
              className={finalInputClassName}
              disabled={disabled}
            />
          )}
        />
      </fieldset>
    </div>
  );
};
